import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Download, Receipt, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TeacherPayslipsSection = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/payslips`, { withCredentials: true });
      setPayslips(data);
    } catch (e) { toast.error('Failed to load payslips'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const downloadPdf = async (p) => {
    try {
      const res = await axios.get(`${API}/api/payslips/${p.id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip_${p.month}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Failed to download PDF'); }
  };

  const money = (v) => `RM ${Number(v || 0).toFixed(2)}`;

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <Card className="bg-white border-slate-200" data-testid="teacher-payslips-section">
      <CardHeader>
        <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>My Payslips</CardTitle>
        <CardDescription>View and download your last 3 months of payslips</CardDescription>
      </CardHeader>
      <CardContent>
        {payslips.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Receipt className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            <p>No payslips available yet</p>
            <p className="text-xs mt-1">Your admin will generate them monthly</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {payslips.slice(0, 3).map((p) => (
                <div key={p.id} className="p-4 border border-slate-200 rounded-lg bg-gradient-to-br from-blue-50 to-white" data-testid={`payslip-card-${p.month}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-blue-100 text-blue-700 border-blue-300 border">{p.month}</Badge>
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Net Pay</p>
                  <p className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{money(p.net_pay)}</p>
                  <p className="text-xs text-slate-500 mt-1">Gross: {money(p.gross_pay)} • Deductions: {money(p.total_deductions)}</p>
                  <Button size="sm" className="w-full mt-3 bg-blue-600 hover:bg-blue-700" onClick={() => downloadPdf(p)} data-testid={`download-payslip-${p.month}`}>
                    <Download className="w-3 h-3 mr-1" /> Download PDF
                  </Button>
                </div>
              ))}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Basic</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">EPF</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead>
                  <TableHead className="text-right">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="teacher-payslips-table">
                {payslips.map((p, idx) => (
                  <TableRow key={p.id} data-testid={`teacher-payslip-row-${idx}`}>
                    <TableCell className="font-medium">{p.month}</TableCell>
                    <TableCell className="text-right">{money(p.basic_salary)}</TableCell>
                    <TableCell className="text-right">{money(p.gross_pay)}</TableCell>
                    <TableCell className="text-right">-{money(p.epf_employee)}</TableCell>
                    <TableCell className="text-right text-red-600">-{money(p.total_deductions)}</TableCell>
                    <TableCell className="text-right font-semibold text-blue-700">{money(p.net_pay)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(p)} data-testid={`download-payslip-btn-${idx}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TeacherPayslipsSection;
